'use client';

import { useEffect } from 'react';

import { useLocalStorage } from '@uidotdev/usehooks';

import { useGetFeatureFlag } from '@/lib/feature-flags/hooks/use-get-feature-flag';
import type { FeatureFlagVariant } from '@/lib/feature-flags/types';

const PASSWORDLESS_SIGN_UP_VARIANT_STORAGE_KEY = '__ab_test_passwordless_sign_up_2026_q3_variant';

interface UsePasswordlessSignUpVariantReturn {
  isPasswordless: boolean;
  variant: FeatureFlagVariant | undefined;
}

// Same read-and-cache shape as `useSignUpQ1Variant`: PostHog wins when it has answered, and the cached
// value covers the gap before it does, so a returning visitor does not watch the password field appear
// and then vanish. Fails closed: only an explicit `experiment` is passwordless.
export function usePasswordlessSignUpVariant(): UsePasswordlessSignUpVariantReturn {
  const [storedVariant, setStoredVariant] = useLocalStorage<FeatureFlagVariant | null>(
    PASSWORDLESS_SIGN_UP_VARIANT_STORAGE_KEY,
    null,
  );

  const posthogVariant = useGetFeatureFlag('ab_test_passwordless_sign_up_2026_q3');
  const resolvedVariant = posthogVariant ?? storedVariant ?? undefined;
  // A flag that is off evaluates to `false`, not to a variant. Report that as "no arm" so it is never
  // stamped onto events or Clerk metadata as if it were one.
  const variant = resolvedVariant === 'control' || resolvedVariant === 'experiment' ? resolvedVariant : undefined;

  useEffect(() => {
    if (posthogVariant === undefined || storedVariant === posthogVariant) {
      return;
    }

    setStoredVariant(posthogVariant);
  }, [storedVariant, posthogVariant, setStoredVariant]);

  return {
    isPasswordless: variant === 'experiment',
    variant,
  };
}
